import React, { useEffect, useState, useCallback } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Link
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, InfoOutlined as InfoIcon, Block as BlockIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Can from '../../components/Can';
import notaCreditoCompraService, { NotaCreditoCompra } from '../../services/notaCreditoCompraService';
import variablesSistemaService from '../../services/variablesSistemaService';
import { formatCurrency, formatDateLocal } from '../../utils/formatters';
import NumeroInternoMeta from '../../components/NumeroInternoMeta';
import { getApiErrorMessage } from '../../utils/apiError';

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const s = formatDateLocal(fecha);
  return s || '-';
};

const formatoLetraSucNro = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro = String(nroComprobante ?? 0).padStart(8, '0');
  return `${L}-${suc}-${nro}`;
};

/** Anulada solo cuando el backend marcó explícitamente Nombre = ANULADO. */
const esNotaCreditoAnulada = (nc: NotaCreditoCompra): boolean =>
  (nc.nombre ?? '').trim().toUpperCase() === 'ANULADO';

const NotaCreditoCompraDetalle: React.FC = () => {
  const navigate = useNavigate();
  const { nroFormulario } = useParams<{ nroFormulario: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [nota, setNota] = useState<NotaCreditoCompra | null>(null);
  const [loading, setLoading] = useState(true);
  const [anulando, setAnulando] = useState(false);
  const [openImpuestosDialog, setOpenImpuestosDialog] = useState(false);
  const [impuestoTasas, setImpuestoTasas] = useState<{
    impuestotasa1?: number;
    impuestotasa2?: number;
    impuestotasa3?: number;
    impuestotasa4?: number;
  }>({});

  const loadNota = useCallback(async () => {
    const id = parseInt(nroFormulario || '', 10);
    if (isNaN(id)) {
      enqueueSnackbar('Nota no válida', { variant: 'error' });
      navigate('/compras/notas-credito');
      return;
    }
    try {
      setLoading(true);
      const data = await notaCreditoCompraService.getNotaCreditoCompra(id);
      setNota(data);
    } catch {
      enqueueSnackbar('Error al cargar la nota de crédito de compra', { variant: 'error' });
      navigate('/compras/notas-credito');
    } finally {
      setLoading(false);
    }
  }, [nroFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    void loadNota();
  }, [loadNota]);

  useEffect(() => {
    variablesSistemaService.getImpuestoTasas().then(setImpuestoTasas).catch(() => {});
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#1976d2' }} />
      </Box>
    );
  }

  if (!nota) return null;

  const anulada = esNotaCreditoAnulada(nota);

  const handleAnular = async () => {
    if (anulando || anulada) return;
    const id = nota.nroFormulario;
    try {
      const check = await notaCreditoCompraService.puedeAnularNotaCreditoCompra(id);
      if (!check.puedeAnular) {
        enqueueSnackbar(check.motivos[0] ?? 'No se puede anular este comprobante', { variant: 'warning' });
        return;
      }
      if (!window.confirm('¿Está seguro que desea anular esta nota de crédito de compra?')) return;

      setAnulando(true);
      await notaCreditoCompraService.anularNotaCreditoCompra(id);
      enqueueSnackbar('Nota de crédito de compra anulada correctamente', { variant: 'success' });
      await loadNota();
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al anular la nota de crédito de compra'), { variant: 'error' });
    } finally {
      setAnulando(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/compras/notas-credito')}
                sx={{ textTransform: 'none' }}
              >
                Notas de Crédito
              </Button>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Nota de Crédito de Compra {formatoLetraSucNro(nota.letra, nota.nroSuc, nota.nroComprobante ?? undefined)}
              </Typography>
              {anulada && (
                <Chip label="Anulada" size="small" color="default" variant="outlined" />
              )}
            </Stack>
            {!anulada && (
              <Can permiso="compras.notas-credito.anular">
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<BlockIcon />}
                  disabled={anulando}
                  onClick={() => void handleAnular()}
                >
                  Anular
                </Button>
              </Can>
            )}
          </Stack>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Encabezado
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Fecha emisión</Typography>
              <Typography variant="body1">{formatearFecha(nota.fechaEmision)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Nro Comprobante</Typography>
              <Typography variant="body1">{formatoLetraSucNro(nota.letra, nota.nroSuc, nota.nroComprobante ?? undefined)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Proveedor</Typography>
              <Typography variant="body1">{nota.nroProveedor} - {nota.nombre || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Forma de pago</Typography>
              <Typography variant="body1">{nota.formaDePago || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Ubicación</Typography>
              <Typography variant="body1">
                {nota.nroUbicacionDestino != null
                  ? nota.ubicacionDestinoDescripcion
                    ? `${nota.nroUbicacionDestino} - ${nota.ubicacionDestinoDescripcion}`
                    : String(nota.nroUbicacionDestino)
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Usuario</Typography>
              <Typography variant="body1">{nota.usuario || '-'}</Typography>
            </Grid>
            {nota.nroFormularioFactura != null && Number(nota.nroFormularioFactura) > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Factura origen</Typography>
                <Link
                  component={RouterLink}
                  to={`/compras/facturas/${nota.nroFormularioFactura}`}
                  underline="hover"
                  sx={{ fontWeight: 600 }}
                >
                  Nº int. {nota.nroFormularioFactura}
                </Link>
              </Grid>
            )}
            {nota.descripcion && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Descripción</Typography>
                <Typography variant="body1">{nota.descripcion}</Typography>
              </Grid>
            )}
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Detalle
          </Typography>
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Cantidad</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Precio lista</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">% Desc</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Precio unit.</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Descuento</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(nota.detalles || []).map((d, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{(d.tipoProveedor ?? '').trim().toUpperCase() === 'G' ? 'Concepto' : 'Producto'}</TableCell>
                    <TableCell>{d.codigoArticulo || '-'}</TableCell>
                    <TableCell>{d.descripcionArticulo || '-'}</TableCell>
                    <TableCell align="right">{Number(d.cantidad)?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) ?? '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(d.precioLista ?? 0)}</TableCell>
                    <TableCell align="right">{Number(d.porcDesc)?.toFixed(1) ?? '-'}%</TableCell>
                    <TableCell align="right">{formatCurrency(d.precioUnitario ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(d.descuento ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(d.subtotal ?? 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Totales
          </Typography>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Subtotal</Typography>
                  <Typography>{formatCurrency(nota.subtotal ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Descuento</Typography>
                  <Typography>{formatCurrency(nota.descuento ?? 0)}</Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Subtotal gravado</Typography>
                  <Typography>{formatCurrency(nota.importeSinIva ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    IVA {impuestoTasas.impuestotasa1 != null ? `${impuestoTasas.impuestotasa1}%` : '1'}
                  </Typography>
                  <Typography>{formatCurrency(nota.ivainscripto ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    IVA {impuestoTasas.impuestotasa2 != null ? `${impuestoTasas.impuestotasa2}%` : '2'}
                  </Typography>
                  <Typography>{formatCurrency(nota.ivanoInscripto ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    IVA {impuestoTasas.impuestotasa3 != null ? `${impuestoTasas.impuestotasa3}%` : '3'}
                  </Typography>
                  <Typography>{formatCurrency(nota.ivaotro ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Exento</Typography>
                  <Typography>{formatCurrency(nota.exento ?? 0)}</Typography>
                </Stack>
                {(nota.percepciones ?? 0) > 0 && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      component="span"
                      color="text.secondary"
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => setOpenImpuestosDialog(true)}
                    >
                      Otros impuestos
                      {(nota.impuestos?.length ?? 0) > 0 && (
                        <IconButton size="small" sx={{ ml: 0.5, py: 0 }} onClick={(e) => { e.stopPropagation(); setOpenImpuestosDialog(true); }}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Typography>
                    <Typography>{formatCurrency(nota.percepciones ?? 0)}</Typography>
                  </Stack>
                )}
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                  <Typography>Importe total</Typography>
                  <Typography>{formatCurrency(nota.importeTotal ?? 0)}</Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
          <NumeroInternoMeta nro={nota.nroFormulario} />
        </CardContent>
      </Card>

      <Dialog open={openImpuestosDialog} onClose={() => setOpenImpuestosDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Otros impuestos</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Cuenta</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Monto base</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Monto impuesto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(nota.impuestos ?? []).map((imp, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{imp.codigoImpuesto ?? '-'}</TableCell>
                    <TableCell align="right">{imp.ctaCtable ?? '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(imp.montoBaseImp ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(imp.montoImpuesto ?? 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImpuestosDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotaCreditoCompraDetalle;
